import Footer from '../Components/Footer';
import { useTranslation } from 'react-i18next';
import {
    Avatar,
    Container,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Card,
    CardContent,
    CardActions,
    CardMedia,
    Button,
    Chip,
    Fade,
    Zoom,
    Slide,
    Paper,
    alpha,
    useMediaQuery,
    Box,
} from '@mui/material';
import {
    Description,
    PictureAsPdf,
    Slideshow,
    VideoLibrary,
    Download,
    Visibility,
    ViewModule,
    ViewList,
    OpenInNew,
    GetApp,
    ArrowBack
} from '@mui/icons-material';
import DrawerAppBar from '../Components/DrawerAppBar';
import Menus from '../Components/Menus';
import TitrePage from '../Components/TitrePage';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Files from "../Database/Documents.json";
import { Document } from '../Models/Document';
import Cover1 from "../Database/Files/Documents/Cover_Présentation de la MUPECI (Livre Blanc).jpg";
import Cover2 from "../Database/Files/Documents/Cover_Mupeci TVC VF master.jpg";
import Cover3 from "../Database/Files/Documents/Cover_Tableau du Compte de Résultat Prévisionnel (2025–2029).jpg";
import Cover4 from "../Database/Files/Documents/Cover_pubi-reportage MUPECI.jpg";

const getIcon = (type: Document['type']) => {
    switch (type) {
        case 'doc': return <Description sx={{ fontSize: 40, color: '#1976d2' }} />;
        case 'pdf': return <PictureAsPdf sx={{ fontSize: 40, color: '#d32f2f' }} />;
        case 'ppt': return <Slideshow sx={{ fontSize: 40, color: '#f57c00' }} />;
        case 'video': return <VideoLibrary sx={{ fontSize: 40, color: '#7b1fa2' }} />;
        default: return <Description sx={{ fontSize: 40, color: '#1976d2' }} />;
    }
};

const getTypeLabel = (type: Document['type']) => {
    switch (type) {
        case 'doc': return 'Document';
        case 'pdf': return 'PDF';
        case 'ppt': return 'Présentation';
        case 'video': return 'Vidéo';
        default: return 'Document';
    }
};

const getTypeColor = (type: Document['type']) => {
    switch (type) {
        case 'doc': return '#1976d2';
        case 'pdf': return '#d32f2f';
        case 'ppt': return '#f57c00';
        case 'video': return '#7b1fa2';
        default: return '#1976d2';
    }
};

function Documentation() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
        const [view, setView] = useState<'mosaic' | 'list'>('mosaic');
    const documents: Document[] = [...Files as Document[]];
    const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
    const [hoveredCard, setHoveredCard] = useState<string | null>(null);

    // Responsive breakpoints
    const isXs = useMediaQuery('(max-width:600px)');
    const isSm = useMediaQuery('(max-width:900px)');
    
    // Gérer le paramètre docId dans l'URL
    const docId = searchParams.get('docId');
    const selectedDocFromUrl = docId ? documents.find(doc => doc.docId === docId) : null;

    useEffect(() => {
        if (selectedDocFromUrl) {
            setSelectedDoc(selectedDocFromUrl);
        }

    }, [selectedDocFromUrl]);

    const handleViewChange = (_: any, nextView: 'mosaic' | 'list') => {
        if (nextView) setView(nextView);
    };

    const handleViewDoc = (doc: Document) => {
        setSelectedDoc(doc);
        // Optionnel : ajouter le docId dans l'URL
        setSearchParams({ docId: doc.docId });
    };

    const handleCloseViewer = () => {
        setSelectedDoc(null);
        // Retirer le paramètre docId de l'URL
        setSearchParams({});
    };

    const handleBackToList = () => {
        setSelectedDoc(null);
        setSearchParams({});
    };

    // Si un docId est spécifié et trouvé, afficher uniquement ce document
    if (selectedDocFromUrl && !selectedDoc) {
        return (
            <div style={{ backgroundColor: "inherit" }}>
                <DrawerAppBar /><br />
                <Menus />
                <TitrePage titre={selectedDocFromUrl.title} lien="Label.Titre.titre68" />
                <Box
                    sx={{
                        mt: isXs ? 2 : 8,
                        mb: isXs ? 2 : 8,
                        px: isXs ? 0 : 2,
                    }}
                >
                    <Container maxWidth="lg">
                        <Fade in timeout={800}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: isXs ? 1.5 : 3,
                                    mb: isXs ? 1.5 : 3,
                                    background: `linear-gradient(135deg, ${alpha(getTypeColor(selectedDocFromUrl.type), 0.05)} 0%, ${alpha(getTypeColor(selectedDocFromUrl.type), 0.1)} 100%)`,
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(getTypeColor(selectedDocFromUrl.type), 0.2)}`,
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={2} mb={3}>
                                    <IconButton
                                        onClick={handleBackToList}
                                        sx={{
                                            backgroundColor: alpha(getTypeColor(selectedDocFromUrl.type), 0.1),
                                            color: getTypeColor(selectedDocFromUrl.type),
                                            '&:hover': {
                                                backgroundColor: alpha(getTypeColor(selectedDocFromUrl.type), 0.2),
                                            }
                                        }}
                                    >
                                        <ArrowBack />
                                    </IconButton>
                                    <Box flexGrow={1}>
                                        <Typography
                                            variant={isXs ? "h5" : "h4"}
                                            sx={{
                                                fontWeight: 600,
                                                color: getTypeColor(selectedDocFromUrl.type),
                                                mb: 1
                                            }}
                                        >
                                            {selectedDocFromUrl.title}
                                        </Typography>
                                        <Chip
                                            label={getTypeLabel(selectedDocFromUrl.type)}
                                            sx={{
                                                backgroundColor: getTypeColor(selectedDocFromUrl.type),
                                                color: 'white',
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    {selectedDocFromUrl.description}
                                </Typography>

                                {/* Preview du document */}
                                <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
                                    {selectedDocFromUrl.type === 'pdf' ? (
                                        <iframe
                                            src={selectedDocFromUrl.url}
                                            width="100%"
                                            height={isXs ? "400px" : "600px"}
                                            title={selectedDocFromUrl.title}
                                            style={{ border: 'none' }}
                                        />
                                    ) : selectedDocFromUrl.type === 'video' ? (
                                        <iframe title={selectedDocFromUrl.title}
                                            src={selectedDocFromUrl.url}
                                            allowFullScreen
                                            allowTransparency
                                            width="100%"
                                            height={isXs ? "300px" : "500px"}
                                            style={{ border: 'none' }}
                                        />
                                    ) : (
                                        <iframe
                                            src={selectedDocFromUrl.url}
                                            width="100%"
                                            height={isXs ? "400px" : "600px"}
                                            title={selectedDocFromUrl.title}
                                            style={{ border: 'none' }}
                                        />
                                    )}
                                </Paper>

                                {/* Actions */}
                                <Box display="flex" gap={2} justifyContent="center" flexDirection={isXs ? "column" : "row"}>
                                    <Button
                                        variant="contained"
                                        size={isXs ? "medium" : "large"}
                                        href={selectedDocFromUrl.url}
                                        download
                                        startIcon={<GetApp />}
                                        sx={{
                                            px: isXs ? 2 : 4,
                                            py: isXs ? 1 : 1.5,
                                            borderRadius: 3,
                                            backgroundColor: getTypeColor(selectedDocFromUrl.type),
                                            width: isXs ? '100%' : undefined,
                                            '&:hover': {
                                                backgroundColor: getTypeColor(selectedDocFromUrl.type),
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 8px 25px ${alpha(getTypeColor(selectedDocFromUrl.type), 0.4)}`
                                            }
                                        }}
                                    >
                                        {t("DOC.telecharger") || "Télécharger"}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size={isXs ? "medium" : "large"}
                                        href={selectedDocFromUrl.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        startIcon={<OpenInNew />}
                                        sx={{
                                            px: isXs ? 2 : 4,
                                            py: isXs ? 1 : 1.5,
                                            borderRadius: 3,
                                            borderColor: getTypeColor(selectedDocFromUrl.type),
                                            color: getTypeColor(selectedDocFromUrl.type),
                                            width: isXs ? '100%' : undefined,
                                            '&:hover': {
                                                borderColor: getTypeColor(selectedDocFromUrl.type),
                                                backgroundColor: alpha(getTypeColor(selectedDocFromUrl.type), 0.1),
                                                transform: 'translateY(-2px)',
                                            }
                                        }}
                                    >
                                        Ouvrir dans un nouvel onglet
                                    </Button>
                                </Box>
                            </Paper>
                        </Fade>
                    </Container>
                </Box>
                <Footer />
            </div>
        );
    }

    return (
        <div style={{ backgroundColor: "inherit" }}>
            <DrawerAppBar /><br />
            <Menus />
            <TitrePage titre={t("DOC.titre1")} lien="Label.Titre.titre68" />
            <Box
                sx={{
                    mt: isXs ? 2 : 8,
                    mb: isXs ? 2 : 8,
                    px: isXs ? 0 : 2,
                }}
            >
                <Box display="flex" flexDirection="row" alignItems="flex-start">
                    {/* {!isXs && (
                        <img style={{ width: "5%", marginLeft: "2%" }} draggable={false} src={EllipseGauche} alt="" />
                    )} */}
                    <Container
                        maxWidth="lg"
                        sx={{
                            px: isXs ? 0.5 : 2,
                            width: '100%',
                        }}
                    >
                        {/* En-tête avec contrôles */}
                        <br /><br /><br /><br /><br />
                        <Fade in timeout={800}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: isXs ? 1.5 : 3,
                                    mb: isXs ? 1.5 : 3,
                                    background: `linear-gradient(135deg, ${alpha('#1976d2', 0.05)} 0%, ${alpha('#f57c00', 0.05)} 100%)`,
                                    borderRadius: 3,
                                    border: `1px solid ${alpha('#1976d2', 0.1)}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        order: isXs ? 10 : 0,
                                        width: isXs ? '100%' : 'auto',
                                        mt: isXs ? 10 : 0,
                                    }}
                                    display="flex"
                                    flexDirection={isXs ? "column" : "row"}
                                    justifyContent="space-between"
                                    alignItems={isXs ? "flex-start" : "center"}
                                    gap={isXs ? 2 : 0}
                                >
                                    <Box>
                                        <Typography
                                            variant={isXs ? "h5" : "h4"}
                                            sx={{
                                                fontWeight: 600,
                                                background: 'linear-gradient(135deg, #1976d2 0%, #f57c00 100%)',
                                                backgroundClip: 'text',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                mb: 1
                                            }}
                                        >
                                            {t("DOC.documentsDisponibles") || "Documents disponibles"}
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                            {documents.length} documents • Cliquez pour prévisualiser ou télécharger
                                        </Typography>
                                    </Box>
                                    <ToggleButtonGroup
                                        value={view}
                                        exclusive
                                        onChange={handleViewChange}
                                        aria-label="view mode"
                                        size={isXs ? "small" : "large"}
                                        sx={{
                                            '& .MuiToggleButton-root': {
                                                border: 'none',
                                                borderRadius: 2,
                                                mx: 0.5,
                                                px: isXs ? 1.5 : 3,
                                                py: isXs ? 0.5 : 1.5,
                                                transition: 'all 0.3s ease-in-out',
                                                fontSize: isXs ? '0.85rem' : '1rem',
                                                '&:hover': {
                                                    backgroundColor: alpha('#1976d2', 0.1),
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: `0 6px 20px ${alpha('#1976d2', 0.2)}`
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: '#1976d2',
                                                    color: 'white',
                                                    '&:hover': {
                                                        backgroundColor: '#1565c0',
                                                    }
                                                }
                                            }
                                        }}
                                    >
                                        <ToggleButton value="mosaic" aria-label="mosaic">
                                            <ViewModule sx={{ mr: 1 }} />
                                            Mosaïque
                                        </ToggleButton>
                                        <ToggleButton value="list" aria-label="list">
                                            <ViewList sx={{ mr: 1 }} />
                                            Liste
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            </Paper>
                        </Fade>

                        {view === 'mosaic' ? (
                            <Grid
                                container
                                spacing={isXs ? 1 : isSm ? 2 : 3}
                                sx={{
                                    width: '100%',
                                }}
                            >
                                {documents.map((doc, index) => (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        lg={3}
                                        key={doc.docId}
                                        sx={{
                                            display: 'flex',
                                        }}
                                    >
                                        <Zoom in timeout={300 + index * 100}>
                                            <Card
                                                onMouseEnter={() => setHoveredCard(doc.docId)}
                                                onMouseLeave={() => setHoveredCard(null)}
                                                sx={{
                                                    width: '100%',
                                                    minWidth: 0,
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                                    cursor: 'pointer',
                                                    borderRadius: 3,
                                                    overflow: 'hidden',
                                                    position: 'relative',
                                                    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                                    border: `1px solid ${alpha('#1976d2', 0.1)}`,
                                                    '&:hover': {
                                                        transform: isXs ? 'none' : 'translateY(-12px) scale(1.02)',
                                                        boxShadow: isXs ? 'none' : `0 20px 40px ${alpha('#1976d2', 0.2)}`,
                                                        '& .card-overlay': {
                                                            opacity: 1,
                                                        },
                                                        '& .card-actions': {
                                                            transform: 'translateY(0)',
                                                            opacity: 1,
                                                        }
                                                    }
                                                }}
                                            >
                                                {/* Type Badge */}
                                                <Chip
                                                    label={getTypeLabel(doc.type)}
                                                    size="small"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 12,
                                                        right: 12,
                                                        backgroundColor: getTypeColor(doc.type),
                                                        color: 'white',
                                                        fontWeight: 600,
                                                        zIndex: 2,
                                                        fontSize: '0.75rem'
                                                    }}
                                                />

                                                {/* Overlay pour les actions */}
                                                <Box
                                                    className="card-overlay"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        background: `linear-gradient(135deg, ${alpha('#1976d2', 0.8)} 0%, ${alpha('#f57c00', 0.8)} 100%)`,
                                                        opacity: 0,
                                                        transition: 'opacity 0.3s ease-in-out',
                                                        zIndex: 1,
                                                    }}
                                                />

                                                {doc.id ? (
                                                    <CardMedia
                                                        component="img"
                                                        height={isXs ? "120" : "180"}
                                                        image={doc.id === "1" ? Cover1 : doc.id === "2" ? Cover2 : doc.id === "3" ? Cover3 : doc.id === "4" ? Cover4 : doc.thumbnail}
                                                        alt={doc.title}
                                                        sx={{
                                                            transition: 'transform 0.4s ease-in-out',
                                                            ...(hoveredCard === doc.id && !isXs && {
                                                                transform: 'scale(1.1)'
                                                            }),
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                ) : (
                                                    <Box
                                                        display="flex"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                        height={isXs ? 120 : 180}
                                                        sx={{
                                                            background: `linear-gradient(135deg, ${alpha(getTypeColor(doc.type), 0.1)} 0%, ${alpha(getTypeColor(doc.type), 0.05)} 100%)`,
                                                            transition: 'all 0.3s ease-in-out',
                                                        }}
                                                    >
                                                        {getIcon(doc.type)}
                                                    </Box>
                                                )}

                                                <CardContent sx={{ flexGrow: 1, p: isXs ? 1.5 : 2.5 }}>
                                                    <Typography
                                                        gutterBottom
                                                        variant="h6"
                                                        component="div"
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: isXs ? '1rem' : '1.1rem',
                                                            mb: 1,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                        }}
                                                    >
                                                        {doc.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: 'vertical',
                                                            lineHeight: 1.5,
                                                        }}
                                                    >
                                                        {doc.description}
                                                    </Typography>
                                                </CardContent>

                                                <CardActions
                                                    className="card-actions"
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: 16,
                                                        left: '50%',
                                                        transform: 'translateX(-50%) translateY(20px)',
                                                        opacity: 0,
                                                        transition: 'all 0.3s ease-in-out',
                                                        zIndex: 3,
                                                        gap: 1,
                                                    }}
                                                >
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewDoc(doc);
                                                        }}
                                                        sx={{
                                                            backgroundColor: '#ffffff',
                                                            color: '#1976d2',
                                                            '&:hover': {
                                                                backgroundColor: '#f5f5f5',
                                                                transform: 'scale(1.1)'
                                                            }
                                                        }}
                                                    >
                                                        <Visibility />
                                                    </IconButton>
                                                    <IconButton
                                                        component="a"
                                                        href={doc.url}
                                                        download
                                                        onClick={(e) => e.stopPropagation()}
                                                        sx={{
                                                            backgroundColor: '#ffffff',
                                                            color: '#f57c00',
                                                            '&:hover': {
                                                                backgroundColor: '#f5f5f5',
                                                                transform: 'scale(1.1)'
                                                            }
                                                        }}
                                                    >
                                                        <Download />
                                                    </IconButton>
                                                </CardActions>
                                            </Card>
                                        </Zoom>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                <List sx={{ p: 0 }}>
                                    {documents.map((doc, index) => (
                                        <Slide key={doc.id} direction="right" in timeout={300 + index * 100}>
                                            <ListItem
                                                sx={{
                                                    py: isXs ? 1 : 2,
                                                    px: isXs ? 1 : 3,
                                                    transition: 'all 0.3s ease-in-out',
                                                    borderBottom: index < documents.length - 1 ? `1px solid ${alpha('#1976d2', 0.1)}` : 'none',
                                                    '&:hover': {
                                                        backgroundColor: alpha('#1976d2', 0.05),
                                                        transform: isXs ? 'none' : 'translateX(8px)',
                                                        borderLeft: isXs ? 'none' : `4px solid ${getTypeColor(doc.type)}`,
                                                    }
                                                }}
                                                secondaryAction={
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        <Chip
                                                            label={getTypeLabel(doc.type)}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: alpha(getTypeColor(doc.type), 0.1),
                                                                color: getTypeColor(doc.type),
                                                                fontWeight: 600,
                                                                mr: 2
                                                            }}
                                                        />
                                                        <IconButton
                                                            onClick={() => handleViewDoc(doc)}
                                                            sx={{
                                                                color: '#1976d2',
                                                                '&:hover': {
                                                                    backgroundColor: alpha('#1976d2', 0.1),
                                                                    transform: 'scale(1.1)'
                                                                }
                                                            }}
                                                        >
                                                            <Visibility />
                                                        </IconButton>
                                                        <IconButton
                                                            component="a"
                                                            href={doc.url}
                                                            download
                                                            sx={{
                                                                color: '#f57c00',
                                                                '&:hover': {
                                                                    backgroundColor: alpha('#f57c00', 0.1),
                                                                    transform: 'scale(1.1)'
                                                                }
                                                            }}
                                                        >
                                                            <Download />
                                                        </IconButton>
                                                    </Box>
                                                }
                                            >
                                                <ListItemAvatar>
                                                    <Avatar
                                                        sx={{
                                                            backgroundColor: alpha(getTypeColor(doc.type), 0.1),
                                                            width: isXs ? 40 : 56,
                                                            height: isXs ? 40 : 56,
                                                            mr: 2
                                                        }}
                                                    >
                                                        {getIcon(doc.type)}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, fontSize: isXs ? '1rem' : undefined }}>
                                                            {doc.title}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography variant="body2" color="text.secondary">
                                                            {doc.description}
                                                        </Typography>
                                                    }
                                                    sx={{ pr: isXs ? 2 : 20 }}
                                                />
                                            </ListItem>
                                        </Slide>
                                    ))}
                                </List>
                            </Paper>
                        )}

                        {/* Modal de visualisation modernisée */}
                        {selectedDoc && (
                            <Fade in timeout={300}>
                                <Box
                                    position="fixed"
                                    top={0}
                                    left={0}
                                    width="100vw"
                                    height="100vh"
                                    sx={{
                                        background: 'rgba(0,0,0,0.8)',
                                        backdropFilter: 'blur(10px)',
                                        zIndex: 1300,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        px: isXs ? 1 : 0,
                                    }}
                                    onClick={handleCloseViewer}
                                >
                                    <Zoom in timeout={300}>
                                        <Paper
                                            elevation={24}
                                            sx={{
                                                p: 0,
                                                borderRadius: 4,
                                                minWidth: isXs ? '90vw' : 400,
                                                maxWidth: '95vw',
                                                maxHeight: '90vh',
                                                overflow: 'hidden',
                                                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                                width: isXs ? '100%' : undefined,
                                            }}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            {/* Header du modal */}
                                            <Box
                                                sx={{
                                                    p: isXs ? 2 : 3,
                                                    background: `linear-gradient(135deg, ${getTypeColor(selectedDoc.type)} 0%, ${alpha(getTypeColor(selectedDoc.type), 0.8)} 100%)`,
                                                    color: 'white'
                                                }}
                                            >
                                                <Box display="flex" alignItems="center" gap={2}>
                                                    {getIcon(selectedDoc.type)}
                                                    <Box flexGrow={1}>
                                                        <Typography variant={isXs ? "h6" : "h5"} fontWeight={600}>
                                                            {selectedDoc.title}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                                            {getTypeLabel(selectedDoc.type)} • Prévisualisation
                                                        </Typography>
                                                    </Box>
                                                    <IconButton
                                                        onClick={handleCloseViewer}
                                                        sx={{
                                                            color: '#ffffff',
                                                            '&:hover': { backgroundColor: alpha('#ffffff', 0.2) }
                                                        }}
                                                    >
                                                        ✕
                                                    </IconButton>
                                                </Box>
                                            </Box>

                                            {/* Contenu du modal */}
                                            <Box sx={{ p: isXs ? 2 : 3 }}>
                                                {selectedDoc.type === 'pdf' ? (
                                                    <iframe
                                                        src={selectedDoc.url}
                                                        width="100%"
                                                        height={isXs ? "300px" : "500px"}
                                                        title={selectedDoc.title}
                                                        style={{ border: 'none', borderRadius: '8px' }}
                                                    />
                                                ) : selectedDoc.type === 'video' ? (
                                                    <iframe title={selectedDoc.title}
                                                        src={selectedDoc.url}
                                                        allowFullScreen
                                                        allowTransparency
                                                        width="100%"
                                                        height={isXs ? "200px" : "400px"}
                                                        style={{ borderRadius: '8px' }}
                                                    />
                                                ) : (
                                                    <iframe
                                                        src={selectedDoc.url}
                                                        width="100%"
                                                        height={isXs ? "300px" : "500px"}
                                                        title={selectedDoc.title}
                                                        style={{ border: 'none', borderRadius: '8px' }}
                                                    />
                                                )}

                                                {/* Actions */}
                                                <Box mt={3} display="flex" gap={2} justifyContent="center" flexDirection={isXs ? "column" : "row"}>
                                                    <Button
                                                        variant="contained"
                                                        size={isXs ? "medium" : "large"}
                                                        href={selectedDoc.url}
                                                        download
                                                        startIcon={<GetApp />}
                                                        sx={{
                                                            px: isXs ? 2 : 4,
                                                            py: isXs ? 1 : 1.5,
                                                            borderRadius: 3,
                                                            backgroundColor: getTypeColor(selectedDoc.type),
                                                            width: isXs ? '100%' : undefined,
                                                            '&:hover': {
                                                                backgroundColor: getTypeColor(selectedDoc.type),
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: `0 8px 25px ${alpha(getTypeColor(selectedDoc.type), 0.4)}`
                                                            }
                                                        }}
                                                    >
                                                        {t("DOC.telecharger") || "Télécharger"}
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size={isXs ? "medium" : "large"}
                                                        href={selectedDoc.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        startIcon={<OpenInNew />}
                                                        sx={{
                                                            px: isXs ? 2 : 4,
                                                            py: isXs ? 1 : 1.5,
                                                            borderRadius: 3,
                                                            borderColor: getTypeColor(selectedDoc.type),
                                                            color: getTypeColor(selectedDoc.type),
                                                            width: isXs ? '100%' : undefined,
                                                            '&:hover': {
                                                                borderColor: getTypeColor(selectedDoc.type),
                                                                backgroundColor: alpha(getTypeColor(selectedDoc.type), 0.1),
                                                                transform: 'translateY(-2px)',
                                                            }
                                                        }}
                                                    >
                                                        Ouvrir dans un nouvel onglet
                                                    </Button>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Zoom>
                                </Box>
                            </Fade>
                        )}
                    </Container>
                    {/* {!isXs && (
                        <img style={{ width: "5%", marginLeft: "2%" }} draggable={false} src={EllipseDroite} alt="" />
                    )} */}
                </Box>
                <br /><br />
                <Footer />
            </Box>
        </div >
    );
}

export default Documentation;